import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext.jsx';
import Badge, { OpenStatusBadge } from '../components/Badge.jsx';
import FollowUpCard from '../components/FollowUpCard.jsx';
import QuickNoteModal from '../components/QuickNoteModal.jsx';
import SalesEmailsLeadsModal from '../components/SalesEmailsLeadsModal.jsx';
import {
  DndContext, closestCenter, KeyboardSensor, PointerSensor,
  useSensor, useSensors,
} from '@dnd-kit/core';
import {
  arrayMove, SortableContext, sortableKeyboardCoordinates,
  rectSortingStrategy, useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const DEFAULT_LAYOUT = [
  'follow_up', 'renewals', 'quotes_awaiting',
  'recent_activity', 'recently_converted', 'pending_config',
  'email_engagement', 'sales_emails', 'quiet_sites'
];

// Salespeople (Sheldon and any future sales hires) see only the panels
// relevant to their work: follow-ups they need to make on the leads they
// own, recent activity on those leads, email engagement on those leads,
// and the per-month sales-email trend. Customer-workflow panels
// (renewals, quotes-awaiting, recently-converted, pending bulloch configs)
// are dropped so they don't render as empty boxes. The server-side
// dashboardController already returns empty payloads for those keys for
// salespeople, but filtering the layout here means the panels never
// show up in Edit-Layout reordering either.
const SALESPERSON_LAYOUT = [
  'follow_up', 'recent_activity', 'email_engagement', 'sales_emails'
];

// Reconcile a saved layout against the canonical panel set so it survives
// panels being added/removed: keep the saved order for keys that still exist,
// drop unknown keys, and append any new panels not yet in the saved order.
// Reconcile against the role-aware base layout: an admin's saved layout
// shouldn't include customer-workflow panels for a salesperson. A
// salesperson's saved layout should also drop any customer-workflow keys
// they somehow saved (defensive — the UI doesn't let them save those,
// but a stale prefs row from an earlier admin login could include them).
function reconcileLayout(saved, baseLayout) {
  const base = Array.isArray(baseLayout) ? baseLayout : DEFAULT_LAYOUT;
  if (!Array.isArray(saved)) return base;
  const known = new Set(base);
  const kept = saved.filter((k) => known.has(k));
  const missing = base.filter((k) => !kept.includes(k));
  const merged = [...kept, ...missing];
  return merged.length ? merged : base;
}

function formatDate(value) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toISOString().slice(0, 10);
}

function formatDateTime(value) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString();
}

function noteTone(type) {
  switch (type) {
    case 'Phone': return 'blue';
    case 'Email': return 'purple';
    case 'Meeting': return 'green';
    case 'Demo': return 'orange';
    default: return 'grey';
  }
}

function truncate(s, max) {
  if (!s) return '';
  return s.length > max ? s.slice(0, max - 1) + '…' : s;
}

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}

function daysUntil(iso, today) {
  if (!iso) return null;
  const a = new Date(iso + 'T00:00:00');
  const b = new Date(today + 'T00:00:00');
  return Math.round((a - b) / (1000 * 60 * 60 * 24));
}

function nowMonthLabel() {
  return new Date().toLocaleString('en-US', { month: 'long', year: 'numeric' });
}

function todayLabel() {
  return new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
}

// Derive up to 3 "do this first" items from the existing dashboard
// payload. No new endpoint -- composites the most-urgent across the
// data we already have: oldest overdue follow-up, soonest at-risk
// renewal (price-increase candidate), oldest awaiting Bulloch reply,
// quote that's been sitting unopened the longest. Dave 2026-06-24:
// "Today's Top 3" triage strip to compress the 9-panel dashboard
// down to a one-glance answer to "what should I do first?".
function deriveTopPriorities(data, isSalesperson, today) {
  const items = [];

  // 1. Most overdue follow-up (server already sorts oldest-first).
  const followUps = Array.isArray(data?.follow_up) ? data.follow_up : [];
  const overdue = followUps
    .filter((f) => f.next_contact && f.next_contact < today)
    .sort((a, b) => String(a.next_contact).localeCompare(String(b.next_contact)));
  if (overdue.length > 0) {
    const top = overdue[0];
    const days = Math.abs(daysUntil(formatDate(top.next_contact), today) || 0);
    const name =
      top.company ||
      [top.first_name, top.last_name].filter(Boolean).join(' ').trim() ||
      'Lead';
    items.push({
      tone: days > 14 ? 'red' : 'amber',
      label: 'Overdue follow-up',
      title: name,
      subtitle: `${days} day${days === 1 ? '' : 's'} overdue${top.current_status ? ` · ${top.current_status}` : ''}`,
      link: `/leads/${top.contact_id || top.id}`,
      action: 'Open lead'
    });
  }

  // 2. Renewal at risk -- closest price-increase candidate. Already
  //    sorted by days_until ASC on the server, so [0] is the most
  //    urgent. Salespeople don't get this signal.
  if (!isSalesperson) {
    const alerts = Array.isArray(data?.price_increase_alerts) ? data.price_increase_alerts : [];
    if (alerts.length > 0) {
      const top = alerts[0];
      const days = Number(top.days_until);
      const rel =
        days < 0 ? `${Math.abs(days)} day${Math.abs(days) === 1 ? '' : 's'} overdue`
        : days === 0 ? 'today'
        : `in ${days} day${days === 1 ? '' : 's'}`;
      items.push({
        tone: days <= 7 ? 'red' : days <= 30 ? 'amber' : 'teal',
        label: 'Renewal at risk',
        title: top.company || `Customer #${top.contact_id}`,
        subtitle: `Renews ${rel} · no price-increase email sent`,
        link: `/customers/${top.contact_id}`,
        action: 'Send price increase'
      });
    }
  }

  // 3. Pending Bulloch configuration (oldest = most stale). Skip for
  //    salespeople.
  if (!isSalesperson) {
    const pending = Array.isArray(data?.pending_configurations) ? data.pending_configurations : [];
    if (pending.length > 0) {
      const top = pending[0];
      items.push({
        tone: 'teal',
        label: 'Awaiting Bulloch reply',
        title: top.company || top.site_name || `Site ${top.site_number || ''}`.trim(),
        subtitle: top.site_name ? `${top.site_name}${top.site_number ? ` · ${top.site_number}` : ''}` : 'Bulloch config request sent',
        link: top.customer_id ? `/customers/${top.customer_id}` : '/customers',
        action: 'View customer'
      });
    }
  }

  // Salesperson fallback: when we have follow-ups but no renewal /
  //                       config sources, fill slots 2 + 3 from the
  //                       next-oldest overdues so the strip still
  //                       shows useful triage.
  if (isSalesperson && overdue.length > 1 && items.length < 3) {
    for (const f of overdue.slice(1, 3)) {
      const days = Math.abs(daysUntil(formatDate(f.next_contact), today) || 0);
      const name =
        f.company ||
        [f.first_name, f.last_name].filter(Boolean).join(' ').trim() ||
        'Lead';
      items.push({
        tone: days > 14 ? 'red' : 'amber',
        label: 'Overdue follow-up',
        title: name,
        subtitle: `${days} day${days === 1 ? '' : 's'} overdue${f.current_status ? ` · ${f.current_status}` : ''}`,
        link: `/leads/${f.contact_id || f.id}`,
        action: 'Open lead'
      });
      if (items.length >= 3) break;
    }
  }

  return items.slice(0, 3);
}

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [quietSites, setQuietSites] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [noteOpen, setNoteOpen] = useState(false);
  const { user } = useAuth();
  const isSalesperson = user?.role === 'salesperson';
  const baseLayout = isSalesperson ? SALESPERSON_LAYOUT : DEFAULT_LAYOUT;
  const [layout, setLayout] = useState(baseLayout);
  // "Outreach by Brand & Month" panel sender filter — 'all' default for
  // admin/staff (firm-wide view). Salesperson is hard-scoped server-side
  // and the UI hides the dropdown for them.
  const [outreachSender, setOutreachSender] = useState('all');
  // Users for the Sent-by dropdown. Same /api/users/options the
  // LeadForm Owner picker and Email Tracking page already use.
  const [outreachUsers, setOutreachUsers] = useState([]);
  useEffect(() => {
    if (isSalesperson) return;
    let cancelled = false;
    axios.get('/api/users/options')
      .then((r) => { if (!cancelled) setOutreachUsers(r.data?.users || []); })
      .catch(() => { /* dropdown still works without names */ });
    return () => { cancelled = true; };
  }, [isSalesperson]);

  // Quiet Sites — separate endpoint because it can be tuned/refreshed
  // independently (and Phase 3 health-check emails will reuse it).
  // Salespeople don't get this panel, so skip the fetch entirely.
  const [quietSitesNonce, setQuietSitesNonce] = useState(0);  // bump to force-reload
  const [syncingXsiteio, setSyncingXsiteio] = useState(false);
  const [syncBanner, setSyncBanner] = useState(null);        // { ok, text } | null
  useEffect(() => {
    if (isSalesperson) return;
    let cancelled = false;
    axios.get('/api/dashboard/quiet-sites?days=14')
      .then((r) => { if (!cancelled) setQuietSites(r.data?.sites || []); })
      .catch(() => { /* panel will just render empty */ });
    return () => { cancelled = true; };
  }, [isSalesperson, quietSitesNonce]);

  // Manual xsiteio sync. Synchronous request -- backend takes ~5s. On
  // success we re-pull Quiet Sites (which is the panel hosting the
  // button) so freshly-quiet sites appear and recently-active ones drop
  // off in one round trip.
  async function runXsiteioSync() {
    if (syncingXsiteio) return;
    setSyncingXsiteio(true);
    setSyncBanner(null);
    try {
      const r = await axios.post('/api/xsiteio/sync');
      const { matched, updated, unchanged, unmatched, missing_unique_id, elapsed_ms } = r.data || {};
      const tail = missing_unique_id ? `, ${missing_unique_id} missing Site ID` : '';
      setSyncBanner({
        ok: true,
        text: `Synced ${matched} sites in ${Math.round((elapsed_ms || 0) / 100) / 10}s — ${updated} changed, ${unchanged} unchanged, ${unmatched} unmatched${tail}.`
      });
      setQuietSitesNonce((n) => n + 1);
    } catch (err) {
      setSyncBanner({
        ok: false,
        text: err.response?.data?.error || err.message || 'sync failed'
      });
    } finally {
      setSyncingXsiteio(false);
    }
  }
  const [editMode, setEditMode] = useState(false);
  // Holds the panel row the user clicked on the Sales Emails panel.
  // When non-null, the drill-down modal is open; null means closed.
  const [salesEmailsRow, setSalesEmailsRow] = useState(null);
  const navigate = useNavigate();
  const today = todayISO();

  function loadDashboard() {
    return axios.get(`/api/dashboard?outreach_sender=${encodeURIComponent(outreachSender)}`).then((res) => setData(res.data));
  }

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    axios.get(`/api/dashboard?outreach_sender=${encodeURIComponent(outreachSender)}`)
      .then((res) => { if (!cancelled) setData(res.data); })
      .catch((err) => { if (!cancelled) setError(err.response?.data?.error || err.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [outreachSender]);

  // Load the user's saved panel order; merge with current default so
  // new panels added after the layout was last saved still appear
  // (appended at the end, where the user can then drag them into place).
  useEffect(() => {
    let cancelled = false;
    axios.get('/api/preferences/dashboard-layout')
      .then((res) => {
        if (!cancelled && Array.isArray(res.data?.layout)) {
          setLayout(reconcileLayout(res.data.layout, baseLayout));
        }
      })
      .catch(() => { /* fall back to default silently */ });
    return () => { cancelled = true; };
  }, []);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  // Save on drop and on reset — debouncing 6 items is overkill.
  async function saveLayout(next) {
    setLayout(next);
    try {
      await axios.put('/api/preferences/dashboard-layout', { layout: next });
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    }
  }

  function handleDragEnd(event) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = layout.indexOf(active.id);
    const newIndex = layout.indexOf(over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    saveLayout(arrayMove(layout, oldIndex, newIndex));
  }

  function resetLayout() {
    saveLayout([...DEFAULT_LAYOUT]);
  }

  if (loading) {
    return <section className="page"><p>Loading dashboard…</p></section>;
  }
  if (error) {
    return (
      <section className="page">
        <div className="page-header">
          <h2>Dashboard</h2>
        </div>
        <p className="error">{error}</p>
      </section>
    );
  }

  const {
    stats, follow_up, recent_notes, pending_configurations, renewals,
    recently_converted, quotes_awaiting, price_increase_alerts = [],
    recent_emails = [], sales_emails_by_month = []
  } = data;

  // Panel id → JSX. The sortable grid renders these in the user's saved
  // order; bodies are unchanged from the original inline panels.
  const PANELS = {
    follow_up: (
      <Panel
        title="Follow-up Today"
        count={follow_up.length}
        link={{ to: '/leads', label: 'View all leads →' }}
      >
        {follow_up.length === 0 ? (
          <p className="panel-empty">Nothing to follow up on right now. 🎉</p>
        ) : (
          <div className="followup-stack">
            {follow_up.map((lead) => (
              <FollowUpCard
                key={lead.id}
                lead={lead}
                todayISO={today}
                onClick={() => navigate(`/leads/${lead.id}`)}
              />
            ))}
          </div>
        )}
      </Panel>
    ),
    renewals: (
      <Panel
        title="Renewals"
        count={renewals.length}
        link={{ to: '/customers', label: 'View customers →' }}
      >
        {renewals.length === 0 ? (
          <p className="panel-empty">No upcoming renewals.</p>
        ) : (
          <ul className="renewal-list">
            {renewals.map((r, i) => {
              // Status arrives pre-computed from the server query. Tone:
              // Overdue → red, Near → amber, Active → green.
              const tone = r.renewal_status === 'Overdue' ? 'red'
                        : r.renewal_status === 'Near'    ? 'amber'
                        :                                  'green';
              const days = r.days_until;
              const rel = days < 0
                ? `${Math.abs(days)} day${Math.abs(days) === 1 ? '' : 's'} overdue`
                : days === 0 ? 'today'
                : `in ${days} day${days === 1 ? '' : 's'}`;
              // Server now returns one row per CUSTOMER. The detail
              // line shows the NEXT module renewing + "+N more"
              // suffix when the customer has multiple modules due in
              // the same window. Multi-site customers also get a
              // "·×N sites" badge.
              const key = `${r.contact_id}`;
              const siteCount = Number(r.site_count) || 0;
              const modulesDue = Number(r.modules_due) || 1;
              const moduleLabel = modulesDue > 1
                ? `${r.next_module || 'Renewal'} · +${modulesDue - 1} more`
                : (r.next_module || 'Renewal');
              return (
                <li key={key} className="renewal-item" onClick={() => navigate(`/customers/${r.contact_id}`)}>
                  <div className={`renewal-date renewal-date-${tone}`}>
                    <span className="renewal-date-value cell-mono">{formatDate(r.renewal_date)}</span>
                    <span className="renewal-date-rel">{rel}</span>
                  </div>
                  <div className="renewal-main">
                    <div className="renewal-company">{r.company || `Customer #${r.contact_id}`}</div>
                    <div className="renewal-site cell-mono">
                      {moduleLabel}
                      {siteCount > 1 ? ` · ×${siteCount} sites` : ''}
                    </div>
                  </div>
                  <span className={`badge badge-${tone}`} style={{ marginLeft: 'auto' }}>
                    {r.renewal_status}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </Panel>
    ),
    quotes_awaiting: (
      <Panel title="Quotes Awaiting Response" count={quotes_awaiting.length}>
        {quotes_awaiting.length === 0 ? (
          <p className="panel-empty">No outstanding quotes.</p>
        ) : (
          <ul className="awaiting-list">
            {quotes_awaiting.map((q) => {
              const waitTone = q.days_waiting >= 14 ? 'red' : q.days_waiting >= 7 ? 'amber' : 'green';
              return (
                <li key={q.id} className="awaiting-item" onClick={() => navigate(`/customers/${q.contact_id}`)}>
                  <div className="awaiting-main">
                    <div className="awaiting-company">{q.company || `Contact #${q.contact_id}`}</div>
                    <div className="awaiting-sent cell-mono">Sent {formatDate(q.sent_at)}</div>
                    <div className="awaiting-open-row">
                      <OpenStatusBadge document={q} />
                      {q.opened_at && (
                        <span className="awaiting-opened-at cell-mono">opened {formatDateTime(q.opened_at)}</span>
                      )}
                    </div>
                  </div>
                  <div className={`awaiting-wait awaiting-wait-${waitTone}`}>
                    <span className="awaiting-wait-days">{q.days_waiting}</span>
                    <span className="awaiting-wait-label">{q.days_waiting === 1 ? 'day' : 'days'}</span>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </Panel>
    ),
    recent_activity: (
      <Panel title="Recent Activity" count={recent_notes.length}>
        {recent_notes.length === 0 ? (
          <p className="panel-empty">No activity yet.</p>
        ) : (
          <ul className="activity-list">
            {recent_notes.map((n) => {
              const dest = n.record_type === 'lead' ? `/leads/${n.contact_id}` : `/customers/${n.contact_id}`;
              const company = n.company || [n.first_name, n.last_name].filter(Boolean).join(' ') || `Contact #${n.contact_id}`;
              return (
                <li key={n.id} className="activity-item" onClick={() => navigate(dest)}>
                  <div className="activity-head">
                    <Badge tone={noteTone(n.type)}>{n.type || 'Other'}</Badge>
                    <span className="activity-company">{company}</span>
                    <span className="activity-date cell-mono">{formatDate(n.note_date)}</span>
                  </div>
                  <div className="activity-text">{truncate(n.text, 120)}</div>
                  {n.user_name && <div className="activity-author">— {n.user_name}</div>}
                </li>
              );
            })}
          </ul>
        )}
      </Panel>
    ),
    recently_converted: (
      <Panel title="Recently Converted">
        {recently_converted.length === 0 ? (
          <p className="panel-empty">No conversions yet.</p>
        ) : (
          <ul className="converted-list">
            {recently_converted.map((c) => (
              <li key={c.id} className="converted-item" onClick={() => navigate(`/customers/${c.id}`)}>
                <span className="converted-date cell-mono">{formatDate(c.conversion_date)}</span>
                <span className="converted-company">{c.company || `Customer #${c.id}`}</span>
                <Badge tone="green">Converted</Badge>
              </li>
            ))}
          </ul>
        )}
      </Panel>
    ),
    pending_config: (
      <Panel title="Pending Configuration" count={pending_configurations.length}>
        {pending_configurations.length === 0 ? (
          <p className="panel-empty">No configuration pending.</p>
        ) : (
          <ul className="config-list">
            {pending_configurations.map((c) => {
              const waitTone = c.days_waiting >= 30 ? 'red' : c.days_waiting >= 7 ? 'amber' : 'green';
              return (
                <li key={c.configuration_id} className="config-item" onClick={() => navigate(`/customers/${c.contact_id}`)}>
                  <div className="config-main">
                    <div className="config-company">{c.company || `Contact #${c.contact_id}`}</div>
                    <div className="config-site cell-mono">
                      {c.site_number ? `Site #${c.site_number}` : 'No site number'}
                    </div>
                  </div>
                  <div className={`config-wait config-wait-${waitTone}`}>
                    <span className="config-wait-days">{c.days_waiting}</span>
                    <span className="config-wait-label">{c.days_waiting === 1 ? 'day' : 'days'}</span>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </Panel>
    ),
    email_engagement: (() => {
      const emails = recent_emails || [];
      const openedCount = emails.filter((e) => e.opened_at).length;
      const totalCount  = emails.length;

      const relTime = (mins) => {
        if (mins == null) return '';
        if (mins < 1)  return 'just now';
        if (mins < 60) return `${mins} min${mins === 1 ? '' : 's'} ago`;
        const hours = Math.floor(mins / 60);
        if (hours < 24) return `${hours} hr${hours === 1 ? '' : 's'} ago`;
        const days = Math.floor(hours / 24);
        return days === 1 ? 'yesterday' : `${days} days ago`;
      };

      return (
        <Panel
          title="Email Engagement"
          count={totalCount}
          link={totalCount > 0
            ? { to: '/email-campaigns', label: `${openedCount}/${totalCount} opened →` }
            : { to: '/email-campaigns', label: 'View all →' }}
        >
          {totalCount === 0 ? (
            <p className="panel-empty">No emails sent yet.</p>
          ) : (
            <ul className="email-engagement-list">
              {emails.map((e) => {
                const opened = !!e.opened_at;
                const destPath = e.record_type === 'lead'
                  ? `/leads/${e.contact_id}`
                  : `/customers/${e.contact_id}`;
                const recipientName = [e.first_name, e.last_name].filter(Boolean).join(' ') || '(no name)';
                const cleanedSubject = (e.subject_label || '').replace(/^email:/, '');
                return (
                  <li
                    key={e.id}
                    className={`email-engagement-item${opened ? ' email-engagement-item-opened' : ''}`}
                    onClick={() => navigate(destPath)}
                  >
                    <div className="email-engagement-main">
                      <div className="email-engagement-recipient">
                        <strong>{recipientName}</strong>
                        {e.company && <span className="email-engagement-company"> · {e.company}</span>}
                      </div>
                      <div className="email-engagement-email cell-mono">{e.recipient_email || ''}</div>
                      <div className="email-engagement-subject">{cleanedSubject}</div>
                      <div className="email-engagement-meta">
                        Sent {relTime(e.minutes_since_sent)}
                        {opened && (
                          <>
                            {' · '}
                            <strong className="email-engagement-opened-time">opened {relTime(e.minutes_since_opened)}</strong>
                            {e.open_count > 1 && <span className="email-engagement-count"> · {e.open_count} opens</span>}
                          </>
                        )}
                      </div>
                    </div>
                    <div className="email-engagement-status">
                      {opened
                        ? <span className="badge badge-green">OPENED</span>
                        : <span className="badge badge-grey">UNOPENED</span>}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </Panel>
      );
    })(),

    // Outreach by Brand & Month — rebuilt 2026-05-27. Source switched
    // from notes to documents so the panel can report opens and open
    // rate alongside the volume. Sent-by dropdown (admin/staff only;
    // salesperson hard-scoped server-side) re-fetches the dashboard
    // when changed. Click a row to drill into the leads modal.
    sales_emails: (
      <Panel
        title="Outreach by Brand & Month"
        count={sales_emails_by_month.reduce((s, r) => s + r.leads, 0)}
        right={!isSalesperson && (
          <select
            value={outreachSender}
            onChange={(e) => setOutreachSender(e.target.value)}
            title="Filter by sender — defaults to all users"
            style={{ fontSize: 'var(--text-small)' }}
          >
            <option value="all">All users</option>
            <option value="me">Me ({user?.name || 'me'})</option>
            {outreachUsers
              .filter((u) => u.id !== user?.id)
              .map((u) => (
                <option key={u.id} value={String(u.id)}>{u.name}</option>
              ))}
          </select>
        )}
      >
        {sales_emails_by_month.length === 0 ? (
          <p className="panel-empty">No emails sent to leads yet.</p>
        ) : (
          <table className="data-table" style={{ width: '100%' }}>
            <thead>
              <tr>
                <th>Month</th>
                <th>Fuel Brand</th>
                <th style={{ textAlign: 'right' }}>Leads</th>
                <th style={{ textAlign: 'right' }}>Opened</th>
                <th style={{ textAlign: 'right' }}>Open %</th>
              </tr>
            </thead>
            <tbody>
              {sales_emails_by_month.map((row, i) => {
                // Tone the open-rate badge so weak campaigns visibly
                // stand out. Thresholds match the rules of thumb in the
                // Opens column note on Email Tracking.
                const rate = row.open_rate_pct ?? 0;
                const rateTone = rate >= 40 ? 'green'
                              : rate >= 20 ? 'amber'
                              :              'red';
                return (
                  <tr
                    key={`${row.month}-${row.fuel_brand}-${i}`}
                    onClick={() => setSalesEmailsRow(row)}
                    style={{ cursor: 'pointer' }}
                    title="Click to see the leads that received emails"
                  >
                    <td className="cell-mono">{row.month_label}</td>
                    <td>{row.fuel_brand}</td>
                    <td className="cell-mono" style={{ textAlign: 'right' }}>{row.leads}</td>
                    <td className="cell-mono" style={{ textAlign: 'right' }}>{row.leads_opened ?? 0}</td>
                    <td className="cell-mono" style={{ textAlign: 'right' }}>
                      <span className={`badge badge-${rateTone}`}>{rate}%</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </Panel>
    ),

    // Quiet Sites — Active sites whose xsiteio_last_login is >14 days
    // old (or never seen). Drives the "has this customer stopped using
    // us?" workflow that Phase 3 will turn into automated health-check
    // emails. Data loaded by a separate useEffect above; salespeople
    // never get this panel.
    quiet_sites: (
      <Panel
        title="Quiet Sites"
        count={quietSites.length}
        right={
          // "Sync xsiteio" button. Lives in the Quiet Sites header
          // because this is the panel that consumes the data; clicking
          // it pulls fresh login/update timestamps for every matched
          // site and refreshes the panel. ~5s round trip.
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {syncBanner && (
              <span
                className="cell-mono"
                style={{
                  fontSize: 11,
                  color: syncBanner.ok ? 'var(--color-good)' : 'var(--color-bad)'
                }}
                title={syncBanner.text}
              >
                {syncBanner.ok ? '✓' : '✕'} {syncBanner.text.length > 60
                  ? syncBanner.text.slice(0, 60) + '…'
                  : syncBanner.text}
              </span>
            )}
            <button
              type="button"
              className="btn-secondary btn-sm"
              onClick={runXsiteioSync}
              disabled={syncingXsiteio}
              title="Pull fresh activity timestamps from xsiteio"
            >
              {syncingXsiteio ? 'Syncing…' : 'Sync xsiteio'}
            </button>
          </div>
        }
      >
        {quietSites.length === 0 ? (
          <p className="panel-empty">All matched sites have logged in recently.</p>
        ) : (
          <ul className="renewal-list">
            {quietSites.slice(0, 12).map((q) => {
              // Tone: never-seen → grey, 14-30d → amber, >30d → red.
              const d = q.days_since_login;
              const tone = d == null   ? 'grey'
                         : d >= 30     ? 'red'
                         : d >= 14     ? 'amber'
                         :               'green';
              const rel = d == null
                ? 'never seen'
                : `${d} day${d === 1 ? '' : 's'} quiet`;
              return (
                <li
                  key={q.site_id}
                  className="renewal-item"
                  onClick={() => navigate(`/customers/${q.customer_id}`)}
                >
                  <div className={`renewal-date renewal-date-${tone}`}>
                    <span className="renewal-date-value cell-mono">
                      {q.site_number || `#${q.site_id}`}
                    </span>
                    <span className="renewal-date-rel">{rel}</span>
                  </div>
                  <div className="renewal-main">
                    <div className="renewal-company">
                      {q.company || q.last_name || `Customer #${q.customer_id}`}
                    </div>
                    <div className="renewal-site cell-mono">
                      {q.site_name || '—'}
                    </div>
                  </div>
                  <span className={`badge badge-${tone}`} style={{ marginLeft: 'auto' }}>
                    {d == null ? 'Never' : `${d}d`}
                  </span>
                </li>
              );
            })}
            {quietSites.length > 12 && (
              <li className="panel-more cell-mono" style={{ padding: '8px 12px', color: 'var(--color-text-dim)' }}>
                + {quietSites.length - 12} more quiet site{quietSites.length - 12 === 1 ? '' : 's'}
              </li>
            )}
          </ul>
        )}
      </Panel>
    ),

    // Note: client_growth and anniversary_spotlight panels were moved to
    // the dedicated Customer History page (sidebar, second item). They
    // felt cramped against the action-oriented panels here.
  };

  return (
    <section className="page">
      <div className="page-header">
        <div>
          <h2>Dashboard</h2>
          <div className="subtitle">{todayLabel()}</div>
        </div>
        <div className="dashboard-edit-controls">
          {editMode && (
            <button type="button" className="btn-text" onClick={resetLayout}>
              Reset to default
            </button>
          )}
          <button
            type="button"
            className={editMode ? 'btn-primary' : 'btn-secondary'}
            onClick={() => setEditMode((e) => !e)}
          >
            {editMode ? 'Done' : 'Edit Layout'}
          </button>
        </div>
      </div>

      {/* Today's Top 3 — one-glance triage strip. Composites from the
          existing dashboard payload (no new endpoint). Empty state is
          a calm "all caught up" line so the absence reads as good news
          not a missing widget. Dave 2026-06-24. */}
      <TopPrioritiesStrip
        items={deriveTopPriorities(data, isSalesperson, today)}
        onNavigate={navigate}
      />

      {/* Row 1 — stat cards. Salesperson sees only the two lead-focused
          tiles ("My Pipeline" / "Overdue"); admin + staff see the full
          four-tile row (customers + quotes-this-month are firm-wide
          metrics that aren't useful to a single salesperson). */}
      <div className="stats-row">
        {!isSalesperson && (
          <StatCard
            label="Active Customers"
            value={stats.active_customers}
            tone="green"
            to="/customers"
          />
        )}
        <StatCard
          label={isSalesperson ? 'My Pipeline' : 'Leads in Pipeline'}
          value={stats.leads_in_pipeline}
          tone="teal"
          to="/leads"
          subtext={stats.leads_overdue > 0 && `${stats.leads_overdue} overdue`}
          subtextTone={stats.leads_overdue > 0 ? 'red' : 'dim'}
        />
        <StatCard
          label={isSalesperson ? 'My Overdue' : 'Leads Overdue'}
          value={stats.leads_overdue}
          tone={stats.leads_overdue > 0 ? 'red' : 'grey'}
          to="/leads"
          subtext={stats.leads_overdue === 0 ? 'All caught up' : 'Needs follow-up'}
          subtextTone={stats.leads_overdue === 0 ? 'green' : 'red'}
        />
        {!isSalesperson && (
          <StatCard
            label="Quotes This Month"
            value={stats.quotes_this_month}
            tone="teal"
            subtext={nowMonthLabel()}
            subtextTone="dim"
          />
        )}
      </div>

      {/* Quick Actions bar */}
      <div className="quick-actions">
        <Link to="/leads/new" className="quick-action quick-action-add">
          <div className="quick-action-icon">+</div>
          <div>
            <div className="quick-action-title">Add New Lead</div>
            <div className="quick-action-sub">Capture a new sales opportunity</div>
          </div>
        </Link>
        <button type="button" className="quick-action quick-action-note" onClick={() => setNoteOpen(true)}>
          <div className="quick-action-icon">✎</div>
          <div>
            <div className="quick-action-title">Log a Note</div>
            <div className="quick-action-sub">Record a call, email, or meeting</div>
          </div>
        </button>
        {!isSalesperson && (
          <Link to="/leads" className="quick-action quick-action-quote">
            <div className="quick-action-icon">$</div>
            <div>
              <div className="quick-action-title">Quote a Lead</div>
              <div className="quick-action-sub">Pick a lead or customer to quote</div>
            </div>
          </Link>
        )}
      </div>

      <QuickNoteModal
        open={noteOpen}
        onClose={() => setNoteOpen(false)}
        onSaved={loadDashboard}
      />

      {/* Dashboard panels — per-user sortable grid (drag to reorder in
          edit mode). Rendered in 3-column rows: 0-2, 3-5, 6+. */}
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={layout} strategy={rectSortingStrategy}>
          <div className={`dashboard-grid${editMode ? ' dashboard-grid-edit' : ''}`}>
            {layout.slice(0, 3).map((id) => (
              <SortablePanel key={id} id={id} editMode={editMode}>
                {PANELS[id]}
              </SortablePanel>
            ))}
          </div>

          {/* Price-increase action alert — full-width, always visible.
              Per Dave 2026-06-03: keep the form on the dashboard at
              all times (even at steady-state with nothing to action)
              so it's obvious nothing's required vs. the panel having
              disappeared due to a bug. Empty state shows a calm
              "all caught up" line inside the same container. */}
          <div className="dashboard-row">
            <div className="price-increase-alert">
              <div className="price-increase-alert-head">
                <h3>Price Increase Action Required</h3>
                <span className="price-increase-alert-count">
                  {price_increase_alerts.length > 0
                    ? `${price_increase_alerts.length} customer${price_increase_alerts.length === 1 ? '' : 's'} · renewal in the next 3 months · never sent · 1+ year customer`
                    : 'No action required — every customer renewing in the next 3 months has either had a price-increase email or is under a year old'}
                </span>
              </div>
              {price_increase_alerts.length > 0 && (
                <ul className="price-increase-alert-list">
                  {price_increase_alerts.map((a) => {
                    const days = a.days_until;
                    const rel = days < 0
                      ? `${Math.abs(days)} day${Math.abs(days) === 1 ? '' : 's'} overdue`
                      : days === 0 ? 'today'
                      : `in ${days} day${days === 1 ? '' : 's'}`;
                    // Dismiss = "I handled this without sending an email"
                    // (phone call, in-person, agreed no increase). Hides
                    // the customer from this list until restored.
                    const handleDismiss = async (e) => {
                      e.stopPropagation();
                      if (!window.confirm(`Mark "${a.company || 'this customer'}" as handled? They'll be hidden from the Price Increase Action Required list.`)) return;
                      try {
                        await axios.post(`/api/customers/${a.contact_id}/dismiss-price-increase-action`);
                        await loadDashboard();
                      } catch (err) {
                        alert(`Failed to dismiss: ${err.response?.data?.error || err.message}`);
                      }
                    };
                    return (
                      <li key={a.contact_id} className="price-increase-alert-item"
                          onClick={() => navigate(`/customers/${a.contact_id}`)}>
                        <div className="price-increase-alert-date">
                          <span className="cell-mono">{formatDate(a.renewal_date)}</span>
                          <span className="price-increase-alert-rel">{rel}</span>
                        </div>
                        <div className="price-increase-alert-main">
                          <div className="price-increase-alert-company">
                            {a.company || `Customer #${a.contact_id}`}
                          </div>
                          <div className="price-increase-alert-status cell-mono">
                            {a.price_increase_email_sent
                              ? `Last sent ${formatDate(a.price_increase_email_sent)}`
                              : 'Email never sent'}
                          </div>
                        </div>
                        <button
                          type="button"
                          className="btn-secondary btn-sm"
                          onClick={handleDismiss}
                          title="Mark as handled (hide from this list)"
                          style={{
                            marginLeft: 'auto',
                            color: 'var(--color-warn)',
                            borderColor: 'var(--color-warn-border)',
                            background: 'var(--color-warn-bg)',
                            padding: '0 var(--space-3)',
                            height: '28px',
                            fontWeight: 'var(--font-semibold)'
                          }}
                        >
                          ✓ Done
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </div>

          <div className={`dashboard-grid${editMode ? ' dashboard-grid-edit' : ''}`}>
            {layout.slice(3, 6).map((id) => (
              <SortablePanel key={id} id={id} editMode={editMode}>
                {PANELS[id]}
              </SortablePanel>
            ))}
          </div>

          {/* Row 3 — any extra panels beyond the original 6 (e.g. the
              Sales Emails to Leads panel added 2026-05-24, or the
              Email Engagement panel). Renders only if the layout has
              more than 6 ids, so dashboards in the steady state still
              look like the original two-row design. */}
          {layout.length > 6 && (
            <div className={`dashboard-grid${editMode ? ' dashboard-grid-edit' : ''}`}>
              {layout.slice(6).map((id) => (
                <SortablePanel key={id} id={id} editMode={editMode}>
                  {PANELS[id]}
                </SortablePanel>
              ))}
            </div>
          )}
        </SortableContext>
      </DndContext>

      {/* Drill-down modal for the Sales Emails panel. Renders only when
          a row has been clicked (salesEmailsRow is non-null). */}
      <SalesEmailsLeadsModal
        open={!!salesEmailsRow}
        row={salesEmailsRow}
        onClose={() => setSalesEmailsRow(null)}
      />
    </section>
  );
}

// Top 3 triage strip (Dave 2026-06-24). Inline-styled so it doesn't
// depend on new CSS. Tone -> colored left-edge bar + matching label
// chip. Empty array -> calm "all caught up" message.
function TopPrioritiesStrip({ items, onNavigate }) {
  const BAR = { red: '#b91c1c', amber: '#b45309', teal: '#0e7490' };
  const BG  = { red: '#fee2e2', amber: '#fef3c7', teal: '#cffafe' };
  const FG  = { red: '#991b1b', amber: '#92400e', teal: '#155e75' };
  const isEmpty = !items || items.length === 0;
  return (
    <div style={{
      background: 'var(--color-surface)',
      border: 'var(--border-width) solid var(--color-border)',
      borderRadius: 'var(--radius-lg)',
      boxShadow: 'var(--shadow-sm)',
      padding: 'var(--card-pad)',
      marginBottom: 'var(--space-4)'
    }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 'var(--space-2)', marginBottom: 'var(--space-3)' }}>
        <h3 style={{ margin: 0, fontSize: 'var(--text-large)' }}>Today's Top 3</h3>
        <span style={{
          fontFamily: 'var(--font-mono)', fontSize: 'var(--text-caption)',
          letterSpacing: 'var(--tracking-wider)', textTransform: 'uppercase',
          color: 'var(--color-text-dim)'
        }}>where to start</span>
      </div>
      {isEmpty ? (
        <div style={{
          padding: 'var(--space-4)', textAlign: 'center',
          color: 'var(--color-good)', fontWeight: 'var(--font-medium)',
          fontSize: 'var(--text-base)'
        }}>
          ✓ You're caught up — nothing urgent today
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: `repeat(${items.length}, 1fr)`, gap: 'var(--space-3)' }}>
          {items.map((it, idx) => (
            <button key={idx} type="button" onClick={() => onNavigate(it.link)} style={{
              display: 'flex', alignItems: 'stretch',
              background: 'var(--color-surface)',
              border: 'var(--border-width) solid var(--color-border)',
              padding: 0, cursor: 'pointer', overflow: 'hidden',
              textAlign: 'left', font: 'inherit'
            }}>
              <div style={{ width: 4, background: BAR[it.tone] || BAR.teal, flex: '0 0 4px' }} />
              <div style={{ padding: 'var(--space-3)', flex: 1, minWidth: 0 }}>
                <div style={{
                  fontFamily: 'var(--font-mono)', fontSize: 'var(--text-caption)',
                  letterSpacing: 'var(--tracking-wider)', textTransform: 'uppercase',
                  color: FG[it.tone] || FG.teal, background: BG[it.tone] || BG.teal,
                  display: 'inline-block', padding: '2px 7px',
                  borderRadius: 'var(--radius-sm)', fontWeight: 'var(--font-semibold)',
                  marginBottom: 'var(--space-2)'
                }}>{it.label}</div>
                <div style={{
                  fontSize: 'var(--text-base)', fontWeight: 'var(--font-semibold)',
                  color: 'var(--color-text)', marginBottom: 4,
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
                }}>{it.title}</div>
                <div style={{ fontSize: 'var(--text-small)', color: 'var(--color-text-dim)', marginBottom: 'var(--space-2)' }}>
                  {it.subtitle}
                </div>
                <div style={{ fontSize: 'var(--text-small)', color: 'var(--color-accent)', fontWeight: 'var(--font-medium)' }}>
                  {it.action} →
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value, tone = 'teal', subtext, subtextTone = 'dim', to }) {
  const inner = (
    <>
      <div className={`stat-accent stat-accent-${tone}`} />
      <div className="stat-body">
        <div className="stat-label">{label}</div>
        <div className={`stat-value stat-value-${tone}`}>{value}</div>
        {subtext && (
          <div className={`stat-sub stat-sub-${subtextTone}`}>{subtext}</div>
        )}
      </div>
    </>
  );
  if (to) {
    return <Link to={to} className="stat-card stat-card-clickable">{inner}</Link>;
  }
  return <div className="stat-card">{inner}</div>;
}

function Panel({ title, count, link, right, children }) {
  return (
    <div className="panel">
      <div className="panel-head">
        <h3>
          {title}
          {count != null && count > 0 && <span className="panel-count">{count}</span>}
        </h3>
        {/* `right` slot — used by panels that need a control (e.g. the
            Outreach panel's Sender dropdown) without crowding the title. */}
        {right && <div className="panel-head-right">{right}</div>}
        {link && <Link to={link.to} className="panel-link">{link.label}</Link>}
      </div>
      <div className="panel-body">{children}</div>
    </div>
  );
}

// Wraps a dashboard panel so it can be dragged within the sortable grid.
// Drag listeners attach only in edit mode; outside it the panel renders
// inert (no grab cursor, no outline, normal click-through behaviour).
function SortablePanel({ id, editMode, children }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id, disabled: !editMode });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`sortable-panel${editMode ? ' sortable-panel-edit' : ''}`}
      {...(editMode ? { ...attributes, ...listeners } : {})}
    >
      {children}
    </div>
  );
}
